import { theme } from '@prophecy/theme';
import { Stack } from '@prophecy/ui-v3/Layout';
import { Tooltip } from '@prophecy/ui-v3/Tooltip';
import { Text } from '@prophecy/ui-v3/Typography';
import styled from 'styled-components';

interface MarkerProps {
  color: string;
  position: number;
  icon?: React.ReactNode;
  label?: string;
  tooltip?: string;
  hideLines?: boolean;
  iconCenterOffset?: number;
}

const MARKER_HEIGHT = 63;
const TEXT_OFFSET_FROM_MARKER = 10;
const LEFT_EDGE_THRESHOLD = 5;
const RIGHT_EDGE_THRESHOLD = 90;

export const Marker = ({
  color,
  position,
  label,
  icon,
  tooltip,
  hideLines = false,
  iconCenterOffset = 0
}: MarkerProps) => {
  return (
    <StyledMarker data-test-id='marker' position={position}>
      {!hideLines && (
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='2'
          height={MARKER_HEIGHT}
          viewBox={`0 0 2 ${MARKER_HEIGHT}`}
          fill='none'>
          <line
            x1='1'
            y1='1'
            x2='1'
            y2='20'
            stroke={color}
            strokeWidth='2'
            strokeLinecap='round'
            strokeDasharray='0.1 4'
          />
          <line
            x1='1'
            y1='24'
            x2='1'
            y2='43'
            stroke={color}
            strokeWidth='2'
            strokeLinecap='round'
            strokeDasharray='0.1 4'
          />
          <line
            x1='1'
            y1='43'
            x2='1'
            y2='62'
            stroke={color}
            strokeWidth='2'
            strokeLinecap='round'
            strokeDasharray='0.1 4'
          />
        </svg>
      )}
      <Tooltip title={tooltip}>
        <span>
          <TextContainer
            direction='horizontal'
            align='center'
            gap={theme.spaces.x10}
            position={position}
            hideLines={hideLines}
            iconCenterOffset={iconCenterOffset}>
            {icon && <MarkerIconContainer>{icon}</MarkerIconContainer>}
            {label && (
              <Text level='sm' tone={theme.colors.gray700}>
                {label}
              </Text>
            )}
          </TextContainer>
        </span>
      </Tooltip>
    </StyledMarker>
  );
};

const StyledMarker = styled.div<{ position: number }>`
  position: absolute;
  top: 0;
  left: ${({ position }) => position}%;
  /* Must clear the progress bar segment's own z-index (theme.zLayer.m, see
     packages/ui-v3/src/ProgressBar/styled.tsx) while staying below the Dialog overlay (zLayer.l). */
  z-index: calc(${theme.zLayer.m} + 1);
`;

const TextContainer = styled(Stack)<{ position: number; hideLines: boolean; iconCenterOffset: number }>`
  position: absolute;
  top: ${({ hideLines, iconCenterOffset }) =>
    hideLines ? iconCenterOffset : MARKER_HEIGHT + TEXT_OFFSET_FROM_MARKER + iconCenterOffset}px;
  cursor: pointer;
  white-space: nowrap;
  z-index: ${theme.zLayer.xs};

  /* Smart positioning to avoid text overflow */
  ${({ position, hideLines }) => {
    if (hideLines) {
      if (position >= RIGHT_EDGE_THRESHOLD) {
        return `
          left: auto;
          right: 0;
          transform: none;
        `;
      }
      return `
        left: 0;
        transform: none;
      `;
    }
    if (position <= LEFT_EDGE_THRESHOLD) {
      return `
        left: 10%;
        transform: translateX(-10%);
      `;
    } else if (position >= RIGHT_EDGE_THRESHOLD) {
      return `
        left: auto;
        right: 0;
        transform: none;
      `;
    } else {
      return `
        left: 50%;
        transform: translateX(-50%);
      `;
    }
  }}
`;

const MarkerIconContainer = styled(Stack)`
  padding: ${theme.spaces.x8};
  background-color: ${theme.colors.white};
  border-radius: 50%;
  border: 1px solid ${theme.colors.gray100};
  box-shadow: ${theme.shadows.md};
`;
